import {
    state,
    clearSession,
    saveSession,
    readStoredSession,
    syncSessionFromStorage,
} from './state';
import type { ApiError } from './types';

/** Ошибка API с HTTP-статусом */
export class ApiRequestError extends Error {
    public readonly status: number;
    /** Машиночитаемый код ошибки от сервера (если есть). */
    public readonly code: string | undefined;

    constructor(message: string, status: number, code?: string) {
        super(message);
        this.name = 'ApiRequestError';
        this.status = status;
        this.code = code;
    }
}

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

interface RequestOptions {
    method?: HttpMethod;
    body?: unknown;
    token?: string | null;
    signal?: AbortSignal;
}

/**
 * Обёртка над fetch. Автоматически:
 *  - добавляет Content-Type и Authorization
 *  - сериализует body в JSON
 *  - парсит ответ
 *  - бросает ApiRequestError при не-2xx
 */
export async function apiRequest<T>(
    path: string,
    options: RequestOptions = {}
): Promise<T> {
    const { method = 'GET', body, token, signal } = options;

    const headers: Record<string, string> = {};
    if (body !== undefined) headers['Content-Type'] = 'application/json';
    if (token) headers['Authorization'] = `Bearer ${token}`;

    const init: RequestInit = {
        method,
        headers,
    };
    if (body !== undefined) init.body = JSON.stringify(body);
    if (signal !== undefined) init.signal = signal;

    let res = await fetch(path, init);

    // Если 401 — пробуем обновить токен и повторить
    if (
        res.status === 401 &&
        options.token &&
        state.refreshToken &&
        !path.includes('/api/auth/refresh')   // ← ЗАЩИТА ОТ РЕКУРСИИ
    ) {
        const newToken = await refreshAccessToken();
        if (newToken) {
            // Повторяем запрос с новым токеном
            const retryHeaders: Record<string, string> = { ...headers };
            retryHeaders['Authorization'] = `Bearer ${newToken}`;
            res = await fetch(path, { ...init, headers: retryHeaders });
        }
    }

    let payload: unknown = null;
    const text = await res.text();
    if (text) {
        try {
            payload = JSON.parse(text);
        } catch {
            payload = { error: text };
        }
    }

    if (!res.ok) {
        const errorPayload =
            payload && typeof payload === 'object' ? (payload as ApiError) : null;
        const message = errorPayload && 'error' in errorPayload
            ? String(errorPayload.error)
            : `HTTP ${res.status}`;
        const code =
            errorPayload && typeof errorPayload.code === 'string'
                ? errorPayload.code
                : undefined;
        throw new ApiRequestError(message, res.status, code);
    }

    return payload as T;
}

/**
 * Загрузка бинарного файла (raw body) с авто-refresh при 401.
 * Используется, например, для разбора реплеев SC2.
 */
export async function apiUpload<T>(
    path: string,
    file: Blob,
    filename: string,
    token?: string | null,
    signal?: AbortSignal,
    contentType = 'application/octet-stream'
): Promise<T> {
    const headers: Record<string, string> = {
        'Content-Type': contentType,
        // HTTP-заголовки должны быть ISO-8859-1 → экранируем имя файла (кириллица и т.п.)
        'X-File-Name': encodeURIComponent(filename),
    };
    if (token) headers['Authorization'] = `Bearer ${token}`;

    const init: RequestInit = { method: 'POST', headers, body: file };
    if (signal !== undefined) init.signal = signal;

    let res = await fetch(path, init);

    if (res.status === 401 && token && state.refreshToken) {
        const newToken = await refreshAccessToken();
        if (newToken) {
            res = await fetch(path, {
                ...init,
                headers: { ...headers, Authorization: `Bearer ${newToken}` },
            });
        }
    }

    let payload: unknown = null;
    const text = await res.text();
    if (text) {
        try {
            payload = JSON.parse(text);
        } catch {
            payload = { error: text };
        }
    }

    if (!res.ok) {
        const errorPayload =
            payload && typeof payload === 'object' ? (payload as ApiError) : null;
        const message = errorPayload && 'error' in errorPayload
            ? String(errorPayload.error)
            : `HTTP ${res.status}`;
        const code =
            errorPayload && typeof errorPayload.code === 'string'
                ? errorPayload.code
                : undefined;
        throw new ApiRequestError(message, res.status, code);
    }

    return payload as T;
}

let isRefreshing = false;
let refreshPromise: Promise<string | null> | null = null;

// Имя распределённой блокировки: обновление токена сериализуется между всеми вкладками,
// чтобы они не переиспользовали turn-based refresh_token (иначе Supabase отзывает сессию).
const REFRESH_LOCK = 'sc2ffa-auth-refresh';

/** Выполняет fn под кросс-вкладочной блокировкой (Web Locks API), если она доступна. */
async function withRefreshLock<T>(fn: () => Promise<T>): Promise<T> {
    const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
    if (locks) return locks.request(REFRESH_LOCK, fn);
    return fn();
}

/**
 * Обновляет access-токен. Возвращает новый access-токен или null.
 * Сессия сбрасывается ТОЛЬКО при явном отказе авторизации (401/403);
 * сетевые сбои и 5xx/429 не должны разлогинивать пользователя.
 */
async function refreshAccessToken(): Promise<string | null> {
    // В рамках одной вкладки не запускаем параллельные обновления.
    if (isRefreshing && refreshPromise) return refreshPromise;

    isRefreshing = true;
    refreshPromise = withRefreshLock(doRefreshAccessToken).finally(() => {
        isRefreshing = false;
        refreshPromise = null;
    });

    return refreshPromise;
}

/**
 * Подхватывает сессию, которую могла записать другая вкладка.
 * Возвращает её access-токен, если он реально свежее текущего.
 */
function adoptStoredSession(): string | null {
    const stored = readStoredSession();
    if (!stored.token || stored.token === state.token) return null;
    // expiresAt может отсутствовать у сессий, созданных старой версией сайта.
    if (stored.expiresAt !== null && stored.expiresAt - Date.now() <= 5_000) {
        return null;
    }
    syncSessionFromStorage();
    return stored.token;
}

async function doRefreshAccessToken(): Promise<string | null> {
    // Другая вкладка могла уже обновить токены, пока мы ждали блокировку, —
    // тогда просто подхватываем свежий access-токен вместо нового запроса.
    const adopted = adoptStoredSession();
    if (adopted) return adopted;

    const stored = readStoredSession();
    const refreshToken = stored.refreshToken ?? state.refreshToken;
    if (!refreshToken) return null;

    let res: Response;
    try {
        res = await fetch('/api/auth/refresh', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ refresh_token: refreshToken }),
        });
    } catch {
        // Сеть/таймаут — сессию НЕ трогаем, попробуем позже.
        return null;
    }

    // Только реальный отказ авторизации означает, что логин больше недействителен.
    if (res.status === 401 || res.status === 403) {
        // Возможная гонка с другой вкладкой (turn-based refresh_token): если она уже
        // успела записать свежую сессию — подхватываем её вместо разлогина.
        const after = adoptStoredSession();
        if (after) return after;
        clearSession();
        return null;
    }
    // Всё остальное (5xx, 429 и т.п.) — временное, сессию сохраняем.
    if (!res.ok) return null;

    let data: {
        access_token?: string;
        refresh_token?: string;
        expires_at?: number;
        user?: { id: string; email?: string; username?: string | null };
    };
    try {
        data = await res.json();
    } catch {
        return null;
    }

    if (!data.access_token || !data.user) return null;

    saveSession(
        {
            id: data.user.id,
            email: data.user.email ?? state.user?.email ?? '',
            username: state.user?.username ?? data.user.username ?? null,
            is_moderator: state.user?.is_moderator ?? false,
            is_admin: state.user?.is_admin ?? false,
            is_ghost: state.user?.is_ghost ?? false,
            can_rate: state.user?.can_rate ?? true,
            player_id: state.user?.player_id ?? null,
            player_name: state.user?.player_name ?? null,
        },
        data.access_token,
        data.refresh_token,
        typeof data.expires_at === 'number' ? data.expires_at * 1000 : undefined
    );
    return data.access_token;
}

// --- Упреждающее обновление токена -----------------------------------------
// Обновляем access-токен заранее, не дожидаясь 401: так пользователь не «выпадает»
// в момент истечения токена и не зависит от конкретного запроса.

let refreshTimer: ReturnType<typeof setTimeout> | null = null;

const REFRESH_MARGIN_MS = 2 * 60 * 1000;    // за 2 минуты до истечения
const MIN_REFRESH_DELAY_MS = 30 * 1000;     // не чаще раза в 30 секунд
const MAX_REFRESH_DELAY_MS = 24 * 60 * 60 * 1000; // защита от абсурдно далёкого exp

/** Планирует следующее упреждающее обновление токена по времени истечения. */
export function scheduleTokenRefresh(): void {
    if (refreshTimer !== null) {
        clearTimeout(refreshTimer);
        refreshTimer = null;
    }

    const { refreshToken, expiresAt } = state;
    if (!refreshToken || expiresAt === null) return;

    const untilRefresh = expiresAt - Date.now() - REFRESH_MARGIN_MS;
    const delay = Math.min(Math.max(untilRefresh, MIN_REFRESH_DELAY_MS), MAX_REFRESH_DELAY_MS);

    refreshTimer = setTimeout(() => {
        void refreshAccessToken().finally(() => scheduleTokenRefresh());
    }, delay);
}

// Вкладка вернулась в фокус с почти истёкшим токеном — обновляем сразу.
if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
        if (
            document.visibilityState === 'visible' &&
            state.refreshToken &&
            state.expiresAt !== null &&
            state.expiresAt - Date.now() < REFRESH_MARGIN_MS
        ) {
            void refreshAccessToken();
        }
    });
}

// Токены изменились (вход/обновление/выход/другая вкладка) — пересчитываем таймер.
window.addEventListener('session:changed', () => scheduleTokenRefresh());
scheduleTokenRefresh();